import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useApi } from 'hooks/useApi';
import { useHideAndSaveProjectInteraction } from 'hooks/useHideAndSaveProjectInteraction';
import CollectionsAPI from 'services/API/CollectionsAPI';
import { Project } from 'types';
import { CategoryEnum } from 'types/SavedProject';
import { trackSaveEvent } from 'utils/analytics';
import type { SavedProject } from 'types/SavedProject';

type TProjectSlug = Project['slug'];
type TSaveProjectParams = {
  projectSlug?: TProjectSlug;
  documentId?: string;
  collectionId: number;
  isUnhidingProject?: boolean;
  currentProjectCategory?: string;
  isSharedCollection?: boolean;
};
type TSavedDictionary = Record<string, SavedProject>;
type TGetSavedProjectIdsResponse = { data: TSavedDictionary; message: string };

export const useSavedProjects = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const {
    optimisticallyRemoveHiddenProject,
    optimisticallyRemoveSavedProject,
    actionSavedProjectTriggerRefetch,
  } = useHideAndSaveProjectInteraction();

  const invalidateQueries = useCallback(
    async (collectionId?: string | number | null) => {
      if (collectionId != null) {
        await queryClient.invalidateQueries({
          queryKey: ['shared-collection-saved-projects-map', String(collectionId)],
        });
      } else {
        queryClient.invalidateQueries({ queryKey: ['all-saved-projects'] });
      }
      actionSavedProjectTriggerRefetch();
    },
    [queryClient, actionSavedProjectTriggerRefetch],
  );

  const updateProjectWithCategoryForSharedCollection = useCallback(
    (category: CategoryEnum) =>
      async ({ collectionId, projectSlug }: TSaveProjectParams) => {
        return api.patch(`/shared-collection/${collectionId}/`, {
          project_slug: projectSlug,
          category,
        });
      },
    [api],
  );

  const saveProjectWithCategory = useCallback(
    (category: CategoryEnum) =>
      async ({ collectionId, projectSlug, documentId }: TSaveProjectParams) => {
        return CollectionsAPI.saveEntityToCollection({
          collectionId,
          projectSlug,
          documentId,
          status: category,
        });
      },
    [api],
  );

  const saveProjectWithCategoryWrapper = (category: CategoryEnum) => {
    return async (params: TSaveProjectParams) => {
      if (params.isSharedCollection) {
        return updateProjectWithCategoryForSharedCollection(category)(params);
      }

      return saveProjectWithCategory(category)(params);
    };
  };

  const getSavedProjectsForSharedCollection = (
    sharedCollectionId: string,
    options?: { enabled?: boolean },
  ) => {
    const savedProjects = useQuery(
      ['shared-collection-saved-projects-map', `${sharedCollectionId}`],
      async (): Promise<TSavedDictionary> => {
        const result = await api.get<TGetSavedProjectIdsResponse | null>(
          `/shared-collection/${sharedCollectionId}/`,
        );

        return result?.data || {};
      },
      { refetchOnWindowFocus: false, enabled: options?.enabled },
    );

    return savedProjects;
  };

  const getAllSavedProjectForUser = useQuery(
    ['all-saved-projects'],
    async (): Promise<TSavedDictionary> => {
      const result = await CollectionsAPI.getAllSavedRecordsForUser();

      return result?.data?.data || {};
    },
    { refetchOnWindowFocus: false },
  );

  const handleSaveProjectMutationSettled = useCallback(
    (
      projectSlug: string | undefined,
      documentId: string | undefined,
      isUnhidingProject?: boolean,
      currentProjectCategory?: string | undefined,
      newCategory?: string | undefined,
      collectionId?: string | number | undefined,
      isSharedCollection?: boolean,
    ) => {
      if (isUnhidingProject && (projectSlug ?? documentId)) {
        optimisticallyRemoveHiddenProject(projectSlug ?? documentId ?? '');
      }
      trackSaveEvent({
        saveProjectNewState: newCategory ?? undefined,
        saveProjectPastState: currentProjectCategory ?? undefined,
        projectId: projectSlug ?? undefined,
      });

      if (isSharedCollection && collectionId != null) {
        invalidateQueries(collectionId);
      } else {
        invalidateQueries();
      }
      if (isSharedCollection && projectSlug) {
        queryClient
          .invalidateQueries({
            queryKey: ['project-shared-lists', projectSlug],
          })
          .catch(() => {});
      }
    },
    [optimisticallyRemoveHiddenProject, trackSaveEvent, invalidateQueries, queryClient],
  );

  const watchProject = useMutation({
    mutationFn: saveProjectWithCategoryWrapper(CategoryEnum.WATCH),
    onSettled: (
      _data,
      _error,
      {
        projectSlug,
        isUnhidingProject,
        currentProjectCategory,
        collectionId,
        documentId,
        isSharedCollection,
      },
    ) => {
      handleSaveProjectMutationSettled(
        projectSlug,
        documentId,
        isUnhidingProject,
        currentProjectCategory,
        CategoryEnum.WATCH,
        collectionId,
        isSharedCollection,
      );
    },
  });

  const starProject = useMutation({
    mutationFn: saveProjectWithCategoryWrapper(CategoryEnum.STAR),
    onSettled: (
      _data,
      _error,
      {
        projectSlug,
        documentId,
        isUnhidingProject,
        currentProjectCategory,
        collectionId,
        isSharedCollection,
      },
    ) => {
      handleSaveProjectMutationSettled(
        projectSlug,
        documentId,
        isUnhidingProject,
        currentProjectCategory,
        CategoryEnum.STAR,
        collectionId,
        isSharedCollection,
      );
    },
  });

  const unsaveProject = useMutation({
    mutationFn: ({ collectionId, projectSlug }: TSaveProjectParams) =>
      api.delete(`/collection/${collectionId}/project/${projectSlug}/`),
    onMutate: async ({
      projectSlug,
      currentProjectCategory,
      documentId,
    }: TSaveProjectParams) => {
      optimisticallyRemoveSavedProject(projectSlug || documentId || '');
      trackSaveEvent({
        saveProjectNewState: 'uncategorized',
        saveProjectPastState: currentProjectCategory,
        projectId: projectSlug,
      });
    },
    onSettled: () => {
      actionSavedProjectTriggerRefetch();
    },
  });

  const selectSavedProjectBySlug = useCallback(
    (projectSlug: TProjectSlug) => getAllSavedProjectForUser?.data?.[projectSlug],
    [getAllSavedProjectForUser.data],
  );

  return {
    getAllSavedProjectForUser,
    getSavedProjectsForSharedCollection,
    watchProject,
    starProject,
    unsaveProject,
    selectSavedProjectBySlug,
  };
};
